<template>
  <aside
    class="fixed inset-y-0 left-0 z-30 flex flex-col transition-all duration-300 border-r
           bg-white border-slate-200
           dark:bg-slate-900 dark:border-slate-800"
    :class="isOpen ? 'w-72' : 'w-20'"
  >
    <div class="h-16 flex items-center justify-center border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
      <div class="flex items-center gap-3 transition-all duration-300" :class="{ 'px-6 w-full justify-center':isOpen }">
        <img
          src="@/assets/img/logo_neurals_nuevo.png"
          alt="Neurals Logo"
          class="transition-all duration-300 object-contain"
          :class="isOpen ? 'h-10' : 'h-8'"
        />
      </div>
    </div>

    <!-- BOTÓN SELECTOR DE MÓDULO -->
    <div class="p-3 border-b border-slate-100 dark:border-slate-800 relative z-20">
      <button
        @click="isOpen ? isModuleMenuOpen = !isModuleMenuOpen : null"
        class="w-full flex items-center gap-3 p-2 rounded-xl transition-all duration-200 border
               bg-slate-50 border-slate-200 hover:border-brand-300 hover:bg-white
               dark:bg-slate-800/50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-800"
        :class="{ 'justify-center': !isOpen, 'justify-between px-3': isOpen }"
      >
        <div class="flex items-center gap-3 overflow-hidden">
          <div
            class="w-6 h-6 rounded flex items-center justify-center text-white shadow-sm flex-shrink-0 transition-colors"
            :class="[currentModule?.bg || 'bg-indigo-600']"
          >
             <component :is="currentModule?.icon" class="w-3.5 h-3.5" />
          </div>

          <div v-if="isOpen" class="text-left truncate">
            <span class="block font-semibold text-xs text-slate-700 dark:text-slate-200 leading-tight truncate">
              {{ currentModule?.name || 'Cargando...' }}
            </span>
          </div>
        </div>

        <ChevronDown
          v-if="isOpen"
          class="w-3.5 h-3.5 text-slate-400 transition-transform duration-200 flex-shrink-0"
          :class="{ 'rotate-180': isModuleMenuOpen }"
        />
      </button>

      <!-- DROPDOWN DE MÓDULOS -->
      <div
        v-if="isModuleMenuOpen && isOpen"
        class="absolute top-full left-3 right-3 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-xl shadow-slate-200/50 dark:shadow-black/50 border border-slate-100 dark:border-slate-700 p-1.5 overflow-hidden animate-fade-in z-50"
      >
        <div class="text-[10px] font-bold text-slate-400 px-2 py-1.5 uppercase tracking-wider">Mis Aplicaciones</div>

        <button
          v-for="(mod, key) in modulesList"
          :key="key"
          @click="switchModule(mod.id); isModuleMenuOpen = false"
          class="w-full flex items-center gap-2 p-2 rounded-lg transition-colors group relative
                 hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <div class="w-6 h-6 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-700 group-hover:text-white group-hover:bg-slate-900 dark:group-hover:bg-slate-600 transition-colors">
             <component :is="mod.icon" class="w-3 h-3" />
          </div>

          <span class="flex-1 text-left text-xs font-medium text-slate-700 dark:text-slate-200">
            {{ mod.name }}
          </span>

          <Check v-if="currentModule?.id === mod.id" class="w-3.5 h-3.5 text-text-white" />
        </button>
      </div>

      <div v-if="isModuleMenuOpen" @click="isModuleMenuOpen = false" class="fixed inset-0 z-10 cursor-default"></div>
    </div>

    <!-- MENÚ NAVEGACIÓN PRINCIPAL -->
    <nav class="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
      <template v-for="(item, index) in currentMenu" :key="index">
        <router-link
          v-if="!item.type"
          :to="item.route"
          class="group flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 relative"
          :class="[
            isRouteActive(item.route)
              ? 'bg-brand-50 text-white font-medium dark:bg-brand-500/10 dark:text-brand-400'
              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
          ]"
        >
          <!-- Barra lateral activa -->
          <div v-if="isRouteActive(item.route) && isOpen" class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-brand-500 rounded-r-full"></div>

          <component
            :is="item.icon"
            class="w-5 h-5 transition-transform group-hover:scale-110 flex-shrink-0"
            :stroke-width="isRouteActive(item.route) ? 2.5 : 2"
          />

          <span v-if="isOpen" class="ml-3 text-sm truncate animate-fade-in">{{ item.label }}</span>

          <div v-if="!isOpen" class="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none z-50 whitespace-nowrap">
            {{ item.label }}
          </div>
        </router-link>

        <div v-else class="my-4 border-t border-slate-100 dark:border-slate-800 mx-2"></div>
      </template>
    </nav>

    <!-- FOOTER PERFIL Y MODO OSCURO -->
    <div class="p-3 border-t border-slate-100 dark:border-slate-800 space-y-2">

      <button
        @click="toggleDarkMode"
        class="w-full flex items-center p-2 rounded-xl transition-all duration-200
               text-slate-500 hover:bg-slate-50 hover:text-slate-700
               dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        :class="{ 'justify-center': !isOpen, 'justify-between bg-slate-50/50 dark:bg-slate-800/30 px-3': isOpen }"
      >
        <div class="flex items-center gap-3">
          <component :is="isDark ? Moon : Sun" class="w-4 h-4" />
          <span v-if="isOpen" class="text-xs font-medium">
            {{ isDark ? 'Modo Oscuro' : 'Modo Claro' }}
          </span>
        </div>

        <div v-if="isOpen" class="w-8 h-4 bg-slate-200 dark:bg-slate-700 rounded-full relative transition-colors">
          <div
            class="absolute top-0.5 left-0.5 w-3 h-3 bg-white dark:bg-slate-400 rounded-full transition-transform shadow-sm"
            :class="{ 'translate-x-4 dark:bg-brand-400': isDark }"
          ></div>
        </div>
      </button>

      <button
        @click="goToProfile"
        class="flex items-center w-full p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
      >
        <div class="relative">
          <img :src="currentUser.photo_url || 'https://i.pravatar.cc/150?u=frontline'" class="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-600" alt="User" />
          <span class="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-slate-900 rounded-full"></span>
        </div>

        <div v-if="isOpen" class="ml-3 text-left overflow-hidden flex-1">
          <p class="text-xs font-bold text-slate-800 dark:text-slate-200 truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            {{ currentUser.full_name || 'Usuario' }}
          </p>
          <p class="text-[10px] text-slate-400 truncate">{{ currentUser.email || 'Sin correo' }}</p>
        </div>

        <LogOut
          @click.stop="handleLogout"
          v-if="isOpen"
          class="w-4 h-4 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400 transition-colors"
        />
      </button>
    </div>

  </aside>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAppModules } from '@/composables/useAppModules';
import { useDarkMode } from '@/composables/useDarkMode';
import { SecureStorage } from '@/utils/secureStorage';
import { Sun, Moon, ChevronDown, Check, LogOut } from 'lucide-vue-next';

const props = defineProps({
  isOpen: { type: Boolean, default: true }
});

const router = useRouter();
const route = useRoute();

const { currentMenu, currentModule, modulesList, switchModule } = useAppModules();
const isModuleMenuOpen = ref(false);
const { isDark, toggleDarkMode } = useDarkMode();

const currentUser = ref({ full_name: 'Cargando...', email: '' });

// Validador reactivo de ruta activa
const isRouteActive = (itemRoute) => {
  if (!itemRoute) return false;
  if (itemRoute === '/' || itemRoute === '/dashboard') {
    return route.path === itemRoute;
  }
  return route.path.startsWith(itemRoute);
};

onMounted(() => {
  const user = SecureStorage.getItem('neurals_user');
  if (user) {
    currentUser.value = user;
  }
});

watch(
  () => route.path,
  (newPath) => {
    const modules = modulesList.value;
    if (!modules) return;

    for (const key in modules) {
      const mod = modules[key];
      if (!mod || !mod.menu) continue;

      const isMatch = mod.menu.some(item =>
        item.route && newPath.startsWith(item.route)
      );

      if (isMatch) {
        if (currentModule.value?.id !== mod.id) {
          switchModule(mod.id);
        }
        break;
      }
    }
  },
  { immediate: true }
);

const goToProfile = () => {
  router.push('/profile');
};

const handleLogout = () => {
  SecureStorage.clear();
  localStorage.removeItem('frontline_token');
  router.push('/login');
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: transparent;
}
.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
}
.dark .custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: #334155;
}
</style>